import {
  AssetType,
  Placement,
  TransformationVariant,
  DeviceFamily,
} from '@amzn/fire-tv-campaign-automation-service';
import { ValidationProps } from 'src/components/pages/campaignAutomation/input/assets';
import { InputType } from 'src/types';

/**
 * Base configuration for all asset types
 */
interface AssetConfig {
  // Asset file properties
  acceptedFileTypes: string;
  allowedContentTypes: string[];
  globalMaxFileSizeInBytes: number;
  assetKeyPrefix: string;
  fileName: string;

  // Display properties
  dataTestId: string;
  displayLabel: string;

  // Validation properties
  validationsByPlacement: Partial<
    Record<Placement, Partial<Record<TransformationVariant, ValidationProps>>>
  >;
}

/**
 * Asset Input configurations by DeviceFamily,Placement and AssetType
 */
const ASSET_INPUT_CONFIG: Record<
  DeviceFamily,
  Partial<Record<Placement, Partial<Record<AssetType, Partial<Record<InputType, boolean>>>>>>
> = {
  [DeviceFamily.FIRE_TV]: {
    [Placement.FEATURE_ROTATOR]: {
      [AssetType.BACKGROUND_IMAGE]: {
        [InputType.SEARCH]: true,
        [InputType.UPLOAD]: true,
        [InputType.URL]: true,
      },
      [AssetType.LOGO]: {
        [InputType.LOGO_TEXT]: true,
        [InputType.UPLOAD]: false,
        [InputType.URL]: false,
      },
      [AssetType.VIDEO_TRAILER]: {
        [InputType.UPLOAD]: true,
        [InputType.URL]: true,
      },
    },
    [Placement.SCREEN_SAVER]: {
      [AssetType.BACKGROUND_IMAGE]: {
        [InputType.SEARCH]: true,
        [InputType.UPLOAD]: true,
        [InputType.URL]: true,
      },
      [AssetType.LOGO]: {
        [InputType.LOGO_TEXT]: true,
        [InputType.UPLOAD]: false,
        [InputType.URL]: false,
      },
    },
  },
  [DeviceFamily.FIRE_TABLETS]: {
    [Placement.FEATURE_ROTATOR]: {
      [AssetType.BACKGROUND_IMAGE]: {
        [InputType.SEARCH]: true,
        [InputType.UPLOAD]: true,
        [InputType.URL]: true,
      },
      [AssetType.LOGO]: {
        [InputType.LOGO_TEXT]: true,
        [InputType.UPLOAD]: false,
        [InputType.URL]: false,
      },
    },
  },
};

/**
 * Default selected input type configuration by DeviceFamily,Placement and AssetType
 */
const DEFAULT_SELECTED_INPUT_TYPE: Record<
  DeviceFamily,
  Partial<Record<Placement, Partial<Record<AssetType, InputType>>>>
> = {
  [DeviceFamily.FIRE_TV]: {
    [Placement.FEATURE_ROTATOR]: {
      [AssetType.BACKGROUND_IMAGE]: InputType.SEARCH,
      [AssetType.LOGO]: InputType.LOGO_TEXT,
      [AssetType.VIDEO_TRAILER]: InputType.UPLOAD,
    },
    [Placement.SCREEN_SAVER]: {
      [AssetType.BACKGROUND_IMAGE]: InputType.SEARCH,
      [AssetType.LOGO]: InputType.LOGO_TEXT,
    },
  },
  [DeviceFamily.FIRE_TABLETS]: {
    [Placement.FEATURE_ROTATOR]: {
      [AssetType.BACKGROUND_IMAGE]: InputType.SEARCH,
      [AssetType.LOGO]: InputType.LOGO_TEXT,
    },
  },
};

/**
 * Configuration for asset files (background image, logo, video)
 */
export const ASSET_CONFIG: Record<AssetType, AssetConfig> = {
  [AssetType.BACKGROUND_IMAGE]: {
    // File properties
    acceptedFileTypes: '.jpeg,.jpg,.png,.JPEG,.JPG,.PNG',
    allowedContentTypes: ['image/jpeg', 'image/png'],
    globalMaxFileSizeInBytes: 30 * 1024 * 1024,
    assetKeyPrefix: 'bgd_images',
    fileName: 'bgd_image',

    // Display properties
    dataTestId: 'asset-input-background-image',
    displayLabel: 'Background Image',

    // Validation properties
    validationsByPlacement: {
      [Placement.FEATURE_ROTATOR]: {
        [TransformationVariant.NO_TRANSFORMATION]: {
          maxFileSizeInBytes: 450 * 1024,
          dimensionSpecification: {
            minResolution: {
              width: 1920,
              height: 720,
            },
            maxResolution: {
              width: 1920,
              height: 720,
            },
          },
        },
        [TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY]: {
          maxFileSizeInBytes: 10 * 1024 * 1024,
          dimensionSpecification: {
            aspectRatio: {
              width: 16,
              height: 9,
            },
            minResolution: {
              width: 1280,
              height: 720,
            },
          },
        },
        [TransformationVariant.FULL_BLEED]: {
          maxFileSizeInBytes: 10 * 1024 * 1024,
          dimensionSpecification: {
            aspectRatio: {
              width: 16,
              height: 9,
            },
            minResolution: {
              width: 1920,
              height: 1080,
            },
          },
        },
        [TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY_FIRE_TABLETS]: {
          maxFileSizeInBytes: 10 * 1024 * 1024,
          dimensionSpecification: {
            aspectRatio: {
              width: 16,
              height: 9,
            },
            minResolution: {
              width: 1280,
              height: 720,
            },
          },
        },
      },
      [Placement.SCREEN_SAVER]: {
        [TransformationVariant.NO_TRANSFORMATION]: {
          maxFileSizeInBytes: 450 * 1024,
          dimensionSpecification: {
            minResolution: {
              width: 1920,
              height: 1080,
            },
            maxResolution: {
              width: 1920,
              height: 1080,
            },
          },
        },
        [TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY]: {
          maxFileSizeInBytes: 10 * 1024 * 1024,
          dimensionSpecification: {
            aspectRatio: {
              width: 16,
              height: 9,
            },
            minResolution: {
              width: 1920,
              height: 1080,
            },
          },
        },
      },
    },
  },

  [AssetType.LOGO]: {
    // File properties
    acceptedFileTypes: '.png',
    allowedContentTypes: ['image/png'],
    globalMaxFileSizeInBytes: 10 * 1024 * 1024,
    assetKeyPrefix: 'logo_images',
    fileName: 'logo',

    // Display properties
    dataTestId: 'asset-input-logo',
    displayLabel: 'Logo',

    // Validation properties
    validationsByPlacement: {
      [Placement.FEATURE_ROTATOR]: {
        [TransformationVariant.NO_TRANSFORMATION]: {
          maxFileSizeInBytes: 450 * 1024,
          dimensionSpecification: {
            minResolution: {
              width: 640,
              height: 260,
            },
            maxResolution: {
              width: 640,
              height: 260,
            },
          },
        },
      },
      [Placement.SCREEN_SAVER]: {
        [TransformationVariant.NO_TRANSFORMATION]: {
          maxFileSizeInBytes: 450 * 1024,
          dimensionSpecification: {
            minResolution: {
              width: 640,
              height: 260,
            },
            maxResolution: {
              width: 640,
              height: 260,
            },
          },
        },
      },
    },
  },

  [AssetType.VIDEO_TRAILER]: {
    // File properties
    acceptedFileTypes: '.mp4,.mov,.mpeg',
    allowedContentTypes: ['video/mp4', 'video/quicktime', 'video/mpeg'],
    globalMaxFileSizeInBytes: 500 * 1024 * 1024,
    assetKeyPrefix: 'video_trailers',
    fileName: 'video_trailer',

    // Display properties
    dataTestId: 'asset-input-video-trailer',
    displayLabel: 'Video Trailer',

    // Validation properties
    validationsByPlacement: {
      [Placement.FEATURE_ROTATOR]: {
        [TransformationVariant.NO_TRANSFORMATION]: {
          maxFileSizeInBytes: 10 * 1024 * 1024,
          dimensionSpecification: {
            aspectRatio: {
              width: 16,
              height: 9,
            },
            minResolution: {
              width: 1920,
              height: 1080,
            },
            maxResolution: {
              width: 1920,
              height: 1080,
            },
          },
          videoLengthSpecs: {
            minDurationInSeconds: 5,
            maxDurationInSeconds: 21,
          },
        },
        [TransformationVariant.CLIPPED_VIDEO]: {
          maxFileSizeInBytes: 500 * 1024 * 1024,
          dimensionSpecification: {
            aspectRatio: {
              width: 16,
              height: 9,
            },
            minResolution: {
              width: 1920,
              height: 1080,
            },
          },
          videoLengthSpecs: {
            minDurationInSeconds: 5,
            maxDurationInSeconds: 120 * 60,
          },
        },
      },
    },
  },
};

export const ASSET_INPUTS: AssetType[] = [
  AssetType.BACKGROUND_IMAGE,
  AssetType.LOGO,
  AssetType.VIDEO_TRAILER,
];

/**
 * Returns the input type options for a given asset type, device family, and placement.
 *
 * @param assetType - The asset type
 * @param deviceFamily - The device family
 * @param placement - The placement type
 * @returns Input type options configuration
 */
export function getInputTypeOptions(
  assetType: AssetType,
  deviceFamily: DeviceFamily,
  placement: Placement,
): Partial<Record<InputType, boolean>> {
  return ASSET_INPUT_CONFIG[deviceFamily]?.[placement]?.[assetType] || {};
}

/**
 * Returns the list of asset inputs to display for a given device family and placement.
 *
 * @param deviceFamily - The device family
 * @param placement - The placement type
 * @returns Array of AssetType values to render as inputs
 */
export function getAssetInputsFromConfig(
  deviceFamily: DeviceFamily,
  placement: Placement,
): AssetType[] {
  const placementConfig = ASSET_INPUT_CONFIG[deviceFamily]?.[placement];
  if (!placementConfig) {
    console.warn(
      `Asset input configuration missing for deviceFamily ${deviceFamily} and placement ${placement}`,
    );
    return [];
  }

  return Object.keys(placementConfig) as AssetType[];
}

/**
 * Returns the default input type for a given asset type, device family, and placement.
 *
 * @param assetType - The asset type
 * @param deviceFamily - The device family
 * @param placement - The placement type
 * @returns Default input type
 */
export function getDefaultInputType(
  assetType: AssetType,
  deviceFamily: DeviceFamily,
  placement: Placement,
): InputType | undefined {
  return DEFAULT_SELECTED_INPUT_TYPE[deviceFamily]?.[placement]?.[assetType];
}

/**
 * Returns validation props for a given asset type, placement, and transformation variant.
 *
 * @param assetType - The asset type
 * @param placement - The placement type
 * @param transformationVariant - The transformation variant
 * @returns Validation properties
 */
export function getValidationProps(
  assetType: AssetType,
  placement: Placement,
  transformationVariant: TransformationVariant,
): ValidationProps | undefined {
  return ASSET_CONFIG[assetType]?.validationsByPlacement?.[placement]?.[transformationVariant];
}
